import { ReactNode } from 'react';

import { GroupHeaderItemComponent } from '../components/GroupHeaderItem';
import { RegularItem } from '../components/RegularItem';
import { SelectAllItem } from '../components/SelectAllItem';
import type { EnhancedItem } from '../hooks/useEnhancedItems';
import type {
  DownshiftItemProps,
  GroupItem,
  RenderMenuItemFunction,
} from '../types';

type RenderMenuItemByTypeParams<Item> = {
  item: EnhancedItem<Item>;
  index: number;
  showCheckbox: boolean;
  itemProps: DownshiftItemProps;
  customRenderMenuItem?: RenderMenuItemFunction<Item>;
  customRenderMenuItemGroupLabel?: (item: GroupItem) => ReactNode;
};

/**
 * Shared utility function for rendering menu items by type.
 * Used by both Autocomplete and MultiAutocomplete components.
 */
export const renderMenuItemByType = <Item,>(
  params: RenderMenuItemByTypeParams<Item>
): ReactNode => {
  const {
    item,
    index,
    showCheckbox,
    itemProps,
    customRenderMenuItem,
    customRenderMenuItemGroupLabel,
  } = params;

  switch (item.itemType) {
    case 'selectAll':
      return (
        <SelectAllItem
          index={index}
          isChecked={item.isSelected}
          isIndeterminate={item.isIndeterminate}
          showCheckbox={showCheckbox}
          itemProps={itemProps}
        />
      );
    case 'groupLabel':
      return (
        <GroupHeaderItemComponent
          enhancedItem={item}
          isHighlighted={itemProps['aria-selected']}
          showCheckbox={showCheckbox}
          customRenderMenuItemGroupLabel={customRenderMenuItemGroupLabel}
          itemProps={itemProps}
        />
      );
    case 'regular':
      return (
        <RegularItem
          enhancedItem={item}
          isHighlighted={itemProps['aria-selected']}
          showCheckbox={showCheckbox}
          customRenderMenuItem={customRenderMenuItem}
          itemProps={itemProps}
          index={index}
        />
      );
    default: {
      // Exhaustive check - this will cause a TypeScript error if we miss any itemType
      const _exhaustiveCheck: never = item;
      return _exhaustiveCheck;
    }
  }
};
